import { useState } from 'react'
import toast from 'react-hot-toast'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import {
  useProjectMembers,
  useProjectInvitations,
  useRemoveProjectMember,
  useUpdateProjectMember,
  useCancelProjectInvitation,
  useInviteProjectMember,
} from '@/hooks/useProjectMembers'
import { MEMBER_ROLE_CONFIG } from '@/types/project'
import type { ProjectMemberRole } from '@/types/project'
import {
  UserPlus,
  Mail,
  Trash2,
  Send,
  Clock,
  X,
  Bell,
  BellOff,
  Users,
} from 'lucide-react'

interface MembersTabProps {
  projectId: string
  projectName: string
}

export function MembersTab({ projectId, projectName }: MembersTabProps) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<ProjectMemberRole>('viewer')
  const [showInviteForm, setShowInviteForm] = useState(false)

  const { data: members, isLoading: loadingMembers } = useProjectMembers(projectId)
  const { data: invitations, isLoading: loadingInvitations } = useProjectInvitations(projectId)
  const inviteMember = useInviteProjectMember()
  const removeMember = useRemoveProjectMember()
  const updateMember = useUpdateProjectMember()
  const cancelInvitation = useCancelProjectInvitation()

  const handleAddOrInvite = async () => {
    if (!email.trim()) {
      toast.error('Veuillez saisir un email')
      return
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      toast.error('Email invalide')
      return
    }

    try {
      const result = await inviteMember.mutateAsync({
        projectId,
        email: email.trim(),
        role,
      })
      if (result.status === 'member_added') {
        toast.success(`${email} ajoute et notifie par email`)
      } else {
        toast.success(`Invitation envoyee a ${email}`)
      }
      setEmail('')
      setShowInviteForm(false)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur lors de l\'ajout'
      toast.error(message)
    }
  }

  const handleRemoveMember = async (memberId: string, memberEmail: string) => {
    if (!confirm(`Retirer ${memberEmail} du projet ?`)) return
    try {
      await removeMember.mutateAsync({ memberId, projectId })
      toast.success(`${memberEmail} retire du projet`)
    } catch {
      toast.error('Erreur lors de la suppression')
    }
  }

  const handleToggleEmails = async (memberId: string, currentValue: boolean) => {
    try {
      await updateMember.mutateAsync({
        memberId,
        projectId,
        receiveEmails: !currentValue,
      })
      toast.success(!currentValue ? 'Notifications email activees' : 'Notifications email desactivees')
    } catch {
      toast.error('Erreur lors de la mise a jour')
    }
  }

  const handleChangeRole = async (memberId: string, newRole: ProjectMemberRole) => {
    try {
      await updateMember.mutateAsync({ memberId, projectId, role: newRole })
      toast.success('Role mis a jour')
    } catch {
      toast.error('Erreur lors de la mise a jour du role')
    }
  }

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await cancelInvitation.mutateAsync({ invitationId, projectId })
      toast.success('Invitation annulee')
    } catch {
      toast.error('Erreur lors de l\'annulation')
    }
  }

  if (loadingMembers || loadingInvitations) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-mint-600" />
          <h3 className="font-semibold text-brown-900">
            Membres du projet ({members?.length || 0})
          </h3>
        </div>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<UserPlus className="w-4 h-4" />}
          onClick={() => setShowInviteForm(!showInviteForm)}
        >
          Ajouter un membre
        </Button>
      </div>

      {/* Add/Invite form */}
      {showInviteForm && (
        <Card>
          <div className="p-4 space-y-4">
            <h4 className="font-medium text-brown-900">Ajouter un membre ou envoyer une invitation</h4>
            <p className="text-sm text-brown-500">
              Si le compte n'existe pas, une invitation par email sera envoyee pour creer un compte.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <input
                  type="email"
                  placeholder="email@exemple.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddOrInvite()}
                />
              </div>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as ProjectMemberRole)}
                className="px-3 py-2 border border-beige-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mint-500 text-sm bg-white"
              >
                <option value="owner">Proprietaire</option>
                <option value="editor">Editeur</option>
                <option value="viewer">Lecteur</option>
              </select>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Send className="w-4 h-4" />}
                  onClick={handleAddOrInvite}
                  isLoading={inviteMember.isPending}
                >
                  Ajouter
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<X className="w-4 h-4" />}
                  onClick={() => {
                    setShowInviteForm(false)
                    setEmail('')
                  }}
                >
                  Annuler
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Members list */}
      <Card>
        <div className="divide-y divide-beige-200">
          {(!members || members.length === 0) ? (
            <div className="p-8 text-center text-brown-500">
              <Users className="w-10 h-10 mx-auto mb-3 text-brown-300" />
              <p>Aucun membre pour ce projet</p>
              <p className="text-sm mt-1">Ajoutez des membres pour leur donner acces au projet</p>
            </div>
          ) : (
            members.map((member) => {
              const roleConfig = MEMBER_ROLE_CONFIG[member.role as ProjectMemberRole]
              return (
                <div key={member.id} className="p-4 flex items-center justify-between hover:bg-cream-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-mint-100 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-mint-600" />
                    </div>
                    <div>
                      <p className="font-medium text-brown-900">{member.user_email}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant={roleConfig.color} size="sm">{roleConfig.label}</Badge>
                        {member.user_id && (
                          <span className="text-xs text-green-600">Compte actif</span>
                        )}
                        {!member.user_id && (
                          <span className="text-xs text-brown-400">Sans compte</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Toggle email notifications */}
                    <button
                      onClick={() => handleToggleEmails(member.id, member.receive_emails)}
                      className={`p-2 rounded-lg transition-colors ${
                        member.receive_emails
                          ? 'text-mint-600 hover:bg-mint-50'
                          : 'text-brown-400 hover:bg-beige-100'
                      }`}
                      title={member.receive_emails ? 'Desactiver les emails' : 'Activer les emails'}
                    >
                      {member.receive_emails ? (
                        <Bell className="w-4 h-4" />
                      ) : (
                        <BellOff className="w-4 h-4" />
                      )}
                    </button>
                    {/* Role selector */}
                    <select
                      value={member.role}
                      onChange={(e) => handleChangeRole(member.id, e.target.value as ProjectMemberRole)}
                      className="text-xs px-2 py-1 border border-beige-300 rounded bg-white"
                    >
                      <option value="owner">Proprietaire</option>
                      <option value="editor">Editeur</option>
                      <option value="viewer">Lecteur</option>
                    </select>
                    {/* Remove */}
                    <button
                      onClick={() => handleRemoveMember(member.id, member.user_email)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Retirer du projet"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </Card>

      {/* Pending invitations */}
      {invitations && invitations.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-brown-900 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" />
            Invitations en attente ({invitations.length})
          </h4>
          <Card>
            <div className="divide-y divide-beige-200">
              {invitations.map((inv) => (
                <div key={inv.id} className="p-4 flex items-center justify-between hover:bg-cream-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Send className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-brown-900">{inv.email}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="gold" size="sm">En attente</Badge>
                        <span className="text-xs text-brown-400">
                          Expire le {new Date(inv.expires_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleCancelInvitation(inv.id)}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Annuler l'invitation"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Email distribution info */}
      <Card>
        <div className="p-4">
          <h4 className="font-medium text-brown-900 flex items-center gap-2 mb-2">
            <Mail className="w-4 h-4 text-mint-600" />
            Distribution des emails
          </h4>
          <p className="text-sm text-brown-500 mb-3">
            Tous les emails lies au projet <strong>{projectName}</strong> seront envoyes aux membres ayant les notifications activees.
          </p>
          <div className="flex flex-wrap gap-2">
            {members?.filter(m => m.receive_emails).map((m) => (
              <span
                key={m.id}
                className="inline-flex items-center gap-1 px-2 py-1 bg-mint-50 text-mint-700 rounded-full text-xs"
              >
                <Mail className="w-3 h-3" />
                {m.user_email}
              </span>
            ))}
            {(!members || members.filter(m => m.receive_emails).length === 0) && (
              <span className="text-sm text-brown-400">Aucun destinataire configure</span>
            )}
          </div>
        </div>
      </Card>
    </div>
  )
}
